import { DownOutlined, LayoutOutlined, LogoutOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import {
  Breadcrumb,
  type BreadcrumbProps,
  Dropdown,
  Flex,
  Layout,
  type MenuProps,
  Space,
  theme,
  Typography,
} from 'antd';
import { ChevronRight } from 'lucide-react';
import { type ReactNode, useContext, useMemo } from 'react';
import { Link, useLocation, useMatches } from 'react-router-dom';
import { BreadcrumbParamsContext, SiderContext } from './index';
import UserAvatar from '../components/user-avatar';
import { AuthContext } from '../contexts/auth-context-provider';
import usePages from '../hooks/use-pages';
import { type ElementOf } from '../types';

const StyledBreadcrumb = styled(Breadcrumb)`
  & li:last-child {
    font-weight: bold;
  }
`;

const getBreadcrumbName = (
  route: ElementOf<BreadcrumbProps['items']>,
  params: any
): string | ElementOf<BreadcrumbProps['items']>['title'] => {
  if (route.title === undefined || route.title === null) {
    return null;
  }
  const paramsKeys = Object.keys(params).join('|');
  return typeof route.title === 'object'
    ? route.title
    : String(route.title).replace(
        new RegExp(`:(${paramsKeys})`, 'g'),
        (replacement, key) => params[key] || replacement
      );
};

function Header(): ReactNode {
  const { token } = theme.useToken();
  const { session } = useContext(AuthContext);
  const location = useLocation();
  const { toggleSiderCollapsed } = useContext(SiderContext);
  const { breadcrumbParams } = useContext(BreadcrumbParamsContext);
  const pages = usePages();
  const matches = useMatches();

  const breadcrumbItems: BreadcrumbProps['items'] = useMemo(() => {
    const items: any[] = [];
    const pagesIds = matches[matches.length - 1].id
      .split('-')
      .map((id) => Number(id))
      .slice(1);
    pagesIds.reduce<(typeof pages)[string]>(
      (acc, cur) => {
        items.push(acc.children?.[cur] ?? acc);
        return acc.children?.[cur] ?? acc;
      },
      { element: <></>, group: '', icon: undefined, name: '', children: Object.values(pages) }
    );
    return items
      .filter((page) => page.name)
      .map((page, idx, arr) => ({
        title: page?.name,
        href: idx !== arr.length - 1 ? matches[idx + 1].pathname : undefined,
      }));
  }, [location.pathname]);

  const userDropdownMenuItems = [
    // {
    //   label: <Link to={'/profile'}>{pages['/profile'].name}</Link>,
    //   icon: pages['/profile'].icon,
    //   key: 'My profile',
    // },
    {
      label: (
        <Link to={'/logout'} state={{ local: true }}>
          Log out
        </Link>
      ),
      danger: true,
      icon: <LogoutOutlined />,
    },
  ] as MenuProps['items'];

  return (
    <Layout.Header style={{ paddingInline: token.paddingXL, paddingBlockEnd: token.paddingMD }}>
      <Flex justify="space-between" align="center">
        <Flex align="center">
          <LayoutOutlined
            style={{ marginInlineEnd: token.marginMD }}
            className="collapse-trigger"
            onClick={() => {
              toggleSiderCollapsed();
            }}
          />
          <StyledBreadcrumb
            itemRender={(route, params) =>
              route.href ? (
                <Link to={route.href}>{getBreadcrumbName(route, params)}</Link>
              ) : (
                getBreadcrumbName(route, params)
              )
            }
            params={breadcrumbParams}
            items={breadcrumbItems}
            separator={<ChevronRight style={{ display: 'flex', alignSelf: 'center' }} />}
          />
        </Flex>
        <Space>
          <Dropdown
            menu={{
              items: userDropdownMenuItems,
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Space direction="horizontal" style={{ cursor: 'pointer', fontSize: '0.7em' }}>
              <UserAvatar session={session} />
              <Typography.Text>
                {[session?.claims.firstName, session?.claims.lastName].join(' ').trim().length > 0
                  ? [session?.claims.firstName, session?.claims.lastName].join(' ').trim()
                  : 'Customer'}
              </Typography.Text>
              <DownOutlined />
            </Space>
          </Dropdown>
        </Space>
      </Flex>
    </Layout.Header>
  );
}

export default Header;
